import { supabase } from '../../lib/supabase'
import { getApprovedLeaveOn } from './assignmentSheetStore'
import { UNKNOWN_OFFICER } from './coverageReport'
import type { DailyRosterEntry, DailyRosterPlacement } from './dailyRosterFigures'

type EntryRow = {
  employee_id: string
  team_id: string
  start_time: string | null
  end_time: string | null
  employees: { display_name: string; employee_identifier: string } | null
  status_codes: { category: string } | null
  assignments: { name: string } | null
  shift_templates: { name: string } | null
}

type PlacementRow = { employee_id: string; title: string; shift_templates: { name: string } | null }

export type DailyRosterData = {
  entries: DailyRosterEntry[]
  placements: Map<string, DailyRosterPlacement>
  onApprovedLeave: Set<string>
}

/**
 * Every schedule entry on the date that the signed-in account can read, the
 * title and shift of each officer's placement on that date, and who is on
 * approved full-day leave. Row-level security limits a supervisor to their own
 * teams.
 */
export async function getDailyRoster(date: string): Promise<DailyRosterData> {
  const [entryResult, placementResult, onApprovedLeave] = await Promise.all([
    supabase
      .from('schedule_entries')
      .select(
        'employee_id, team_id, start_time, end_time, employees(display_name, employee_identifier), status_codes(category), assignments(name), shift_templates(name)',
      )
      .eq('date', date),
    supabase
      .from('employee_assignments')
      .select('employee_id, title, shift_templates(name)')
      .eq('active', true)
      .lte('effective_from', date)
      .or(`effective_to.is.null,effective_to.gte.${date}`),
    getApprovedLeaveOn(date),
  ])
  if (entryResult.error || placementResult.error) throw new Error('The daily roster could not be loaded.')

  // The employee record is readable only while the officer is on a team the
  // caller supervises, so an officer who has since transferred is still listed
  // on the date they worked, without a name.
  const entries = ((entryResult.data ?? []) as unknown as EntryRow[]).map((row) => ({
    employeeId: row.employee_id,
    identifier: row.employees?.employee_identifier ?? '',
    employee: row.employees?.display_name ?? UNKNOWN_OFFICER,
    teamId: row.team_id,
    statusCategory: row.status_codes?.category ?? '',
    shift: row.shift_templates?.name ?? null,
    startTime: row.start_time,
    endTime: row.end_time,
    assignment: row.assignments?.name ?? null,
  }))
  const placements = new Map<string, DailyRosterPlacement>(
    ((placementResult.data ?? []) as unknown as PlacementRow[]).map((row) => [row.employee_id, { title: row.title, shift: row.shift_templates?.name ?? null }]),
  )
  return { entries, placements, onApprovedLeave }
}
